/**
 * Robi — the WLB help buddy chat. Same chat surface as the Messages thread
 * screen, but backed by the shared Robi conversation + chat hooks and with
 * an intro state that offers starter questions about the app.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import useRobiChat from '@wlb/shared/hooks/useRobiChat';
import useRobiConversation from '@wlb/shared/hooks/useRobiConversation';
import { generateRobiMessageId } from '@wlb/shared/constants';
import { resolveProfilePictureUrl } from '@wlb/shared/utils/imageUrl';
import type { ExtendedChatMessage } from '@wlb/shared/types/chat';
import { HeartIcon, RobiIcon } from '../components/ui/icons';
import { Button } from '../components/ui/Button';
import { ChatBubble } from '../features/messages/ChatBubble';
import { ChatInput } from '../features/messages/ChatInput';
import { ChatSkeleton } from '../features/messages/ChatSkeleton';
import { buildChatTimeline } from '../features/messages/chatTimeline';
import { ChatDaySeparator } from '../features/messages/ChatDaySeparator';
import '../features/messages/messages.css';
import '../features/robi/robi.css';

/** Robi replies asynchronously; stop waiting if nothing lands in this window. */
const REPLY_TIMEOUT_MS = 45000;

export function Robi() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    conversationId,
    conversationDetails,
    loading: conversationLoading,
    error: conversationError,
    missingAiAgent,
    retry,
  } = useRobiConversation();

  const { messages, currentUser, handleSend } = useRobiChat({ conversationId });

  const [inputValue, setInputValue] = useState('');
  const [awaitingReply, setAwaitingReply] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const replyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Participant order is not guaranteed, so identify the agent as the member
  // that is not the signed-in user rather than trusting an index.
  const agent = useMemo(() => {
    const participants = conversationDetails?.participants ?? [];
    return (
      participants.find(
        participant => String(participant.userId) !== String(currentUser._id),
      ) ?? null
    );
  }, [conversationDetails?.participants, currentUser._id]);

  // Agents are addressed by handle, not given name, so the header leads with
  // userName to match how the AI buddy is named everywhere else.
  const headerTitle =
    agent?.userName?.trim() || t('home.tabs.robi');
  const agentAvatar = resolveProfilePictureUrl(agent?.profileImageUrl);

  const timeline = useMemo(() => buildChatTimeline(messages), [messages]);

  const starterQuestions = useMemo(
    () => [
      t('robi.quickReplies.howToPost'),
      t('robi.quickReplies.howBuddyWorks'),
    ],
    [t],
  );

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !conversationId) return;

      const outgoing: ExtendedChatMessage = {
        _id: generateRobiMessageId(),
        text: trimmed,
        createdAt: new Date(),
        user: currentUser,
      };
      handleSend([outgoing]);
      setInputValue('');
      setAwaitingReply(true);
    },
    [conversationId, currentUser, handleSend],
  );

  // The hook refetches the thread after sending, so an incoming message from
  // anyone but the current user means Robi has answered.
  useEffect(() => {
    const latest = messages[0];
    if (!latest) return;
    if (String(latest.user?._id) !== String(currentUser._id)) {
      setAwaitingReply(false);
    }
  }, [messages, currentUser._id]);

  useEffect(() => {
    if (!awaitingReply) return;
    replyTimerRef.current = setTimeout(() => setAwaitingReply(false), REPLY_TIMEOUT_MS);
    return () => {
      if (replyTimerRef.current) clearTimeout(replyTimerRef.current);
    };
  }, [awaitingReply]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, awaitingReply]);

  const renderBody = () => {
    if (missingAiAgent) {
      return (
        <div className="robi-empty">
          <RobiIcon size={48} className="robi-empty__icon" />
          <p className="robi-empty__title">{t('robi.noAiBuddy.title')}</p>
          <p className="robi-empty__desc">{t('robi.noAiBuddy.description')}</p>
          <Button size="sm" onClick={() => navigate('/onboarding/ai-buddy')}>
            {t('sidebar.menu.chooseAiBuddy')}
          </Button>
        </div>
      );
    }

    if (conversationError) {
      return (
        <div className="chat-error">
          <p className="chat-error__text">{t('robi.errors.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={retry}>
            {t('chat.retry')}
          </Button>
        </div>
      );
    }

    if (conversationLoading && messages.length === 0) {
      return <ChatSkeleton />;
    }

    return (
      <div className="chat-messages" role="log" aria-live="polite">
        {messages.length === 0 && (
          <div className="robi-intro">
            <RobiIcon size={40} className="robi-intro__icon" aria-hidden />
            <p className="robi-intro__message">{t('robi.welcomeMessage')}</p>
            <div className="robi-intro__starters">
              {starterQuestions.map(question => (
                <button
                  key={question}
                  type="button"
                  className="robi-starter"
                  onClick={() => send(question)}
                  disabled={!conversationId}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {timeline.map(item =>
          item.type === 'day' ? (
            <ChatDaySeparator key={item.key} date={item.date} />
          ) : (
            <ChatBubble
              key={item.key}
              message={item.message}
              isSent={String(item.message.user?._id) === String(currentUser._id)}
            />
          ),
        )}

        {awaitingReply && (
          <div className="chat-bubble-row chat-bubble-row--received">
            <div className="chat-bubble chat-bubble--received robi-typing" role="status">
              <span className="sr-only">{t('robi.thinking')}</span>
              <span className="robi-typing__dot" aria-hidden />
              <span className="robi-typing__dot" aria-hidden />
              <span className="robi-typing__dot" aria-hidden />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    );
  };

  return (
    <section className="chat-page" aria-label={headerTitle}>
      <div className="chat-header">
        <div className="chat-header__left">
          <div className="chat-header__identity">
            <div className="chat-header__avatar-wrap">
              {agentAvatar ? (
                <img src={agentAvatar} className="chat-header__avatar" alt="" aria-hidden />
              ) : (
                <span className="chat-header__avatar-fallback robi-avatar" aria-hidden>
                  <RobiIcon size={24} />
                </span>
              )}
            </div>
            <div className="chat-header__info">
              <span className="chat-header__name">{headerTitle}</span>
              <span className="chat-header__status robi-header__tagline">
                {t('robi.yourAiBuddy')}
                <HeartIcon size={12} fill="currentColor" className="robi-header__heart" aria-hidden />
              </span>
            </div>
          </div>
        </div>
      </div>

      {renderBody()}

      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSend={() => send(inputValue)}
        sending={awaitingReply || !conversationId}
      />
    </section>
  );
}
